// real
import {
  Avatar,
  Badge,
  Button,
  Flex,
  Text,
  IconButton,
  useToast,
  AlertTitle,
} from "components-lib";
import { MdVideoCall } from "react-icons/md";
import { Lock as LockIcon, Unlock as UnlockIcon } from "lucide-react";
import { FiEdit, } from "react-icons/fi";
import { AmbulanceIcon } from "hugeicons-react";

import { formatTime12Hour } from "utils/dateUtils";
import { axiosInstance } from "config/api";
import axios from "axios";
import { generateBillPdf, uploadFile } from "../components/Bill";


const formatDisplayValue = (value) => {
  if (!value) {
    return "—";
  }

  if (typeof value === "string" && value.trim() === "") {
    return "—";
  }

  return value;
};

const formatDateValue = (value) => {
  if (!value) {
    return "—";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return formatDisplayValue(value);
  }

  return parsed.toLocaleDateString();
};

const getStatusBadgeProps = (status) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();

  switch (normalized) {
    case "active":
      return { colorScheme: "green", label: "Active" };
    case "inactive":
    case "disabled":
    case "blocked":
      return { colorScheme: "red", label: status || "Inactive" };
    case "pending":
    case "invited":
      return { colorScheme: "orange", label: status || "Pending" };
    default:
      return {
        colorScheme: "gray",
        label: status ? String(status) : "—",
      };
  }
};
// processed exports are appended after raw definitions

const columnsDataCheckRaw = [
  {
    Header: "PATIENT ID",
    accessor: "id",
  },
  {
    Header: "NAME",
    accessor: "name",
  },
  {
    Header: "GENDER",
    accessor: "gender",
  },
  {
    Header: "AGE",
    accessor: "age",
  },
  {
    Header: "EMAIL",
    accessor: "email",
  },
  {
    Header: "PHONE NO",
    accessor: "number",
  },
  {
    Header: "PAST APPOINMENTS",
    accessor: "past_appointments",
  },
  {
    Header: "LAB TESTS",
    accessor: "lab_tests",
  },
  {
    Header: "BOOK APPOINMENTS",
    accessor: "book_appointments",
  },
];



// Common cell style to keep content readable without forcing truncation
// and left-align content horizontally (from `col-data.jsx`)
const _cellStyle = {
  whiteSpace: "normal",
  overflow: "visible",
  margin: "0",
  display: "flex",
  flexDirection: "column",
  /* Allow the cell wrapper to grow and shrink so textual content
    can take available space relative to fixed-size buttons. Setting
    minWidth: 0 is required so flex items can shrink below their
    content's min intrinsic size. */
  flex: "1 1 auto",
  minWidth: 0,
  alignItems: "flex-start",
  justifyContent: "flex-start",
  textAlign: "left",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  // Prevent breaking long strings mid-word; wrap only at natural break points
  overflowWrap: "normal",
  wordBreak: "normal",
};

const applyDefaultCell = (cols) =>
  cols.map((col) => {
    const c = { ...col };

    // If a column already provides a Cell, wrap it to apply the common cell style
    if (typeof c.Cell === "function") {
      const Orig = c.Cell;
      c.Cell = (props) => <div style={_cellStyle}>{Orig(props)}</div>;
      return c;
    }

    // Otherwise provide a default Cell which formats the display value
    c.Cell = ({ value }) => <div style={_cellStyle}>{formatDisplayValue(value)}</div>;
    return c;
  });

// processed exports are appended after raw definitions

const columnsDataConsultationRaw = [
  {
    Header: "QUEUE NO",
    accessor: "queue",
    minWidth: 100,
    width: 100,
    maxWidth: 120,
  },
  {
    Header: "PATIENT NAME",
    accessor: "name",
    minWidth: 200,
    width: 200,
    maxWidth: 250,
  },
  {
    Header: "AGE",
    accessor: "age",
    minWidth: 80,
    width: 80,
    maxWidth: 100,
  },
  {
    Header: "SEX",
    accessor: "gender",
    minWidth: 80,
    width: 80,
    maxWidth: 100,
  },
  {
    Header: "MOBILE NO.",
    accessor: "mobile_no",
    minWidth: 150,
    width: 150,
    maxWidth: 180,
  },
  {
    Header: "TYPE",
    accessor: "type",
    minWidth: 120,
    width: 120,
    maxWidth: 150,
    Cell: ({ value, row }) => {
      const isOnline = String(value || "").toLowerCase() === "online";
      const hasMeetLink = !!(
        row?.original?.meet_link ||
        row?.original?.meeting_link ||
        row?.original?.meetLink ||
        row?.original?.meetUrl
      );
      return (
        <Flex alignItems="center" gap={2}>
          <Text>{value}</Text>
          {(isOnline || hasMeetLink) && (
            <IconButton
              icon={<MdVideoCall style={{ fontSize: "1rem" }} />}
              aria-label="Join Video Call"
              size="sm"
              colorScheme="blue"
              variant="ghost"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                // Robustly get the meeting link from the row data supporting multiple key names
                const meetLink =
                  row?.original?.meet_link ||
                  row?.original?.meeting_link ||
                  row?.original?.meetLink ||
                  row?.original?.meetUrl ||
                  null;

                if (meetLink) {
                  // Open the meeting link in a new tab
                  window.open(meetLink, "_blank", "noopener,noreferrer");
                  console.log("Opening meeting link in new tab:", meetLink);
                } else {
                  console.warn(
                    "No meeting link available for this consultation",
                  );
                  // You could show a toast notification here if needed
                }
              }}
              title={
                row?.original?.meet_link ||
                  row?.original?.meeting_link ||
                  row?.original?.meetLink ||
                  row?.original?.meetUrl
                  ? "Join Video Call"
                  : "No meeting link available"
              }
              isDisabled={!hasMeetLink}
              cursor={hasMeetLink ? "pointer" : "not-allowed"}
            />
          )}
        </Flex>
      );
    },
  },
  {
    Header: "BOOKED BY",
    accessor: "frontdesk_email",
    // minWidth: 150,
    // width: 150,
    // maxWidth: 180,
  },
  {
    Header: "SERVICE",
    accessor: "service",
    minWidth: 150,
    width: 150,
    maxWidth: 180,
  },
  {
    Header: "STATUS",
    accessor: "status",
    minWidth: 120,
    width: 120,
    maxWidth: 150,
    Cell: ({ value }) => {
      // Normalize the status value
      const normalizedStatus = String(value || "")
        .toUpperCase()
        .trim();

      // Define status colors and labels
      let color = "gray";
      let label = normalizedStatus;

      switch (normalizedStatus) {
        case "BOOKED":
          color = "yellow";
          label = "BOOKED";
          break;
        case "IN_PROGRESS":
          color = "blue";
          label = "IN PROGRESS";
          break;
        case "COMPLETED":
          color = "green";
          label = "COMPLETED";
          break;
        case "ARRIVED":
          color = "purple";
          label = "ARRIVED";
          break;
        default:
          color = "orange";
          label = normalizedStatus || "UNKNOWN";
      }

      return (
        <Button
          colorScheme={color}
          size="sm"
          variant="solid"
          cursor="default"
          _hover={{ transform: "none" }}
          _active={{ transform: "none" }}
          pointerEvents="none"
        >
          {label}
        </Button>
      );
    },
  },
];

const columnsDataComplexRaw = [
  {
    Header: "PATIENT ID",
    accessor: "id",
    minWidth: 120,
    width: 120,
  },
  {
    Header: "NAME",
    accessor: "name",
    minWidth: 150,
    width: 150,
  },
  {
    Header: "GENDER",
    accessor: "gender",
    minWidth: 100,
    width: 100,
  },
  {
    Header: "AGE",
    accessor: "age",
    minWidth: 50,
    width: 50,
  },
  {
    Header: "EMAIL",
    accessor: "email",
    minWidth: 160,
    width: 160,
  },
  {
    Header: "PHONE NO",
    accessor: "phoneNumber",
    minWidth: 130,
    width: 130,
  },
  {
    Header: "PAST APPOINTMENTS",
    accessor: "past_appointments",
    minWidth: 180,
    width: 180,
  },
  {
    Header: "LAB TESTS",
    accessor: "lab_tests",
    minWidth: 150,
    width: 150,
  },
  {
    Header: "BOOK APPOINTMENTS",
    accessor: "book_appointments",
    minWidth: 180,
    width: 180,
  },
  {
    Header: "EDIT",
    accessor: "edit",
    minWidth: 80,
    width: 80,
    maxWidth: 100,
    Cell: ({ row }) => (
      <IconButton
        icon={<EditIcon />}
        aria-label="Edit Patient"
        size="sm"
        colorScheme="blue"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (row.original.onEdit) {
            row.original.onEdit(row.original);
          }
        }}
      />
    ),
  },
  {
    Header: "DELETE",
    accessor: "delete",
    minWidth: 100,
    width: 100,
    Cell: ({ row }) => (
      <IconButton
        icon={<div style={{ fontSize: "1rem", textAlign: "center", color: "white" }}>X</div>}
        size="sm"
        colorScheme="red"
        variant="solid"
        aria-label="Delete Patient"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Support both handler names for backward compatibility
          const handler = row.original.handleDelete || row.original.onDelete;
          if (typeof handler === "function") {
            handler(row.original);
          }
        }}
      />
    ),
  },
  {
    Header: "EMERGENCY",
    accessor: "emergency",
    minWidth: 100,
    width: 100,
    Cell: ({ row }) => {
      return (
        <IconButton
          icon={<AmbulanceIcon size={16} />}
          aria-label="Emergency Appointment"
          size="sm"
          colorScheme="red"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (row.original.book_appointments) {
              row.original.book_appointments(true);
            }
          }}
        />
      );
    },
  },
];

const clinicColumnsDataRaw = [
  {
    Header: "Logo",
    accessor: "clinicIconURL",
    disableSortBy: true,
    Cell: ({ value, row }) => (
      <Avatar size="sm" src={value || ""} name={row?.original?.clinicName} />
    ),
  },
  {
    Header: "Clinic",
    accessor: "clinicName",
    Cell: ({ value }) => (
      <Text fontWeight="600">{formatDisplayValue(value)}</Text>
    ),
  },
  {
    Header: "Email",
    accessor: "clinicEmail",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "Phone",
    accessor: "phone",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "WhatsApp",
    accessor: "whatsapp",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "Address",
    accessor: "address",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "UPI Details",
    accessor: "upiDetails",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "Bank Details",
    accessor: "bankDetails",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  // {
  //   Header: "Created",
  //   accessor: "createdAt",
  //   Cell: ({ value }) => formatDateValue(value),
  // },
  {
    Header: "Actions",
    accessor: "actions",
    disableSortBy: true,
    Cell: ({ row }) => (
      <Flex gap={2} justifyContent="flex-start">
        <IconButton
          icon={<FiEdit />}
          aria-label="Edit Clinic"
          size="sm"
          colorScheme="blue"
          // variant="ghost"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (typeof row?.original?.onEdit === "function") {
              row.original.onEdit();
            }
          }}
        />
        {(() => {
          const isLocked = !!row?.original?.isLocked;
          return (
            <IconButton
              icon={isLocked ? <UnlockIcon size={16} /> : <LockIcon size={16} />}
              aria-label={isLocked ? "Unlock Clinic" : "Lock Clinic"}
              size="sm"
              colorScheme={isLocked ? "green" : "red"}
              // variant="ghost"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                // Prefer an explicit onUnlock handler when unlocking; fall back to onDelete
                if (isLocked && typeof row?.original?.onUnlock === "function") {
                  row.original.onUnlock();
                  return;
                }

                if (typeof row?.original?.onDelete === "function") {
                  row.original.onDelete();
                }
              }}
            />
          );
        })()}
      </Flex>
    ),
  },
];

const frontdeskColumnsDataRaw = [
  {
    Header: "Avatar",
    accessor: "avatarUrl",
    disableSortBy: true,
    Cell: ({ value, row }) => (
      <Avatar size="sm" src={value || ""} name={row?.original?.name} />
    ),
  },
  {
    Header: "Name",
    accessor: "name",
    Cell: ({ value }) => (
      <Text fontWeight="600">{formatDisplayValue(value)}</Text>
    ),
  },
  {
    Header: "Email",
    accessor: "email",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "Phone",
    accessor: "phone",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "WhatsApp",
    accessor: "whatsapp",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  // {
  //   Header: "Status",
  //   accessor: "status",
  //   Cell: ({ value }) => {
  //     const { colorScheme, label } = getStatusBadgeProps(value);
  //     if (!value) {
  //       return formatDisplayValue(value);
  //     }
  //     return (
  //       <Badge colorScheme={colorScheme} variant="subtle">
  //         {label}
  //       </Badge>
  //     );
  //   },
  // },
  // {
  //   Header: "Created",
  //   accessor: "createdAt",
  //   Cell: ({ value }) => formatDateValue(value),
  // },
  {
    Header: "Actions",
    accessor: "actions",
    disableSortBy: true,
    Cell: ({ row }) => (
      <Flex gap={2} justifyContent="flex-start">
        <IconButton
          icon={<FiEdit />}
          aria-label="Edit Frontdesk"
          size="sm"
          colorScheme="blue"
          variant="solid"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (typeof row?.original?.onEdit === "function") {
              row.original.onEdit();
            }
          }}
        />
        <IconButton
          icon={<div style={{ fontSize: "1rem", color: "white" }}>X</div>}
          size="sm"
          colorScheme="red"
          variant="solid"
          aria-label="Delete Frontdesk"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (typeof row?.original?.onDelete === "function") {
              row.original.onDelete();
            }
          }}
        />
      </Flex>
    ),
  },
];

const appointmentcolumnsDataComplexRaw = [
  {
    Header: "DATE",
    accessor: "created_date",
    Cell: ({ value }) => {
      if (!value) return "";
      try {
        const date = new Date(value);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = String(date.getFullYear());
        return <Text color="gray.500">{`${day}-${month}-${year}`}</Text>;
      } catch (error) {
        return <Text color="gray.500">{value}</Text>;
      }
    },
    minWidth: 40,
    width: 40,
    maxWidth: 80,
  },
  {
    Header: "PATIENT",
    accessor: "name",
    minWidth: 100,
    width: 100,
    maxWidth: 100,
  },
  {
    Header: "DOCTOR",
    accessor: "doctor_name",
    minWidth: 100,
    width: 100,
    maxWidth: 100,
  },
  {
    Header: "AGE",
    accessor: "age",
    minWidth: 20,
    width: 20,
    maxWidth: 40,
  },
  {
    Header: "SEX",
    accessor: "gender",
    minWidth: 20,
    width: 20,
    maxWidth: 50,
  },
  {
    Header: "MOBILE NO.",
    accessor: "phoneNumber",
    minWidth: 90,
    width: 90,
    maxWidth: 90,
  },
  {
    Header: "APPOINTMENT TYPE",
    accessor: "treatment_type",
    minWidth: 140,
    width: 140,
    maxWidth: 120,
    Cell: ({ value }) => (
      <Text>{value === "in_clinic" ? "In Clinic" : "Online"}</Text>
    ),
  },
  {
    Header: "APPOINTMENT TIME",
    accessor: "booking_time",
    Cell: ({ value }) => formatTime12Hour(value),
  }, {
    Header: "Prescription",
    accessor: "prescription",
    Cell: ({ value, row }) => (
      <Flex justifyContent="flex-start" alignItems="center" height="100%">
        <Button
          colorScheme="purple"
          size="sm"
          py={0}
          height="24px"
          minHeight="24px"
          isDisabled={!row.original}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (typeof row?.original?.onPrescriptionClick === "function") {
            row.original.onPrescriptionClick(row.original);
            }
          }}
        >
          View PDF
        </Button>
      </Flex>
    ),
  },
  {
    Header: "parameters",
    accessor: "parameters",
    Cell: ({ value }) => (
      <Flex justifyContent="flex-start" alignItems="left" height="100%">
        <Button
          colorScheme="yellow"
          size="sm"
          py={0}
          height="24px"
          minHeight="24px"
        >
          Add
        </Button>
      </Flex>
    ),
  },
  {
    Header: "Payment Receipt",
    accessor: "payment_receipt",
  },
  {
    Header: "PAYMENT ACTION",
    accessor: "payment_action",
  },
  {
    Header: "Bill Invoice",
    accessor: "bill_invoice",
  },
  {
    Header: "STATUS",
    accessor: "status",
    Cell: ({ value, row }) => {
      const toast = useToast();
      let color = "gray";
      let label = value;
      let nextStatus = value;

      // Normalize the status value
      const normalizedStatus = String(value).toUpperCase().trim();

      // Define status transitions
      switch (normalizedStatus) {
        case "BOOKED":
          color = "yellow";
          nextStatus = "ARRIVED";
          break;
        case "ARRIVED":
          color = "purple";
          nextStatus = "IN_PROGRESS";
          break;
        case "IN_PROGRESS":
          color = "blue";
          nextStatus = "COMPLETED";
          break;
        case "COMPLETED":
          color = "green";
          nextStatus = "COMPLETED"; // No further transition
          break;
        case "CANCELLED":
          color = "gray";
          nextStatus = "CANCELLED"; // No further transition
        case "MISSED":
          color = "red";
          nextStatus = "MISSED"; // No further transition
        default:
          color = "gray"; //INCORRECT Logic
          nextStatus = normalizedStatus;
      }

      const handleStatusClick = async (e) => {
        e.stopPropagation();

        // Don't allow transitions from terminal states
        if (["COMPLETED", "INCOMPLETE"].includes(normalizedStatus)) {
          return;
        }

        // Determine the next status based on current status
        let targetStatus = nextStatus;
        if (normalizedStatus === "ARRIVED") {
          if (
            !window.confirm(
              "Are you sure you want to change status back to BOOKED?",
            )
          ) {
            return; // User cancelled the status change
          }
          targetStatus = "BOOKED";
        } else if (normalizedStatus === "BOOKED") {
          if (!window.confirm("Mark this appointment as ARRIVED?")) {
            return; // User cancelled the status change
          }
          targetStatus = "ARRIVED";
        } else {
          return; // Only handle BOOKED and ARRIVED statuses
        }

        try {
          const response = await axiosInstance.post(
            "/api/appointment/updateApptStatus",
            {
              id: row.original.id,
              status: targetStatus,
            },
          );

          // Check if the response exists and has data
          if (response && response.data) {
            // Show success toast
            toast({
              title: "Status Updated",
              description: `Appointment status changed from ${normalizedStatus} to ${targetStatus}`,
              status: "success",
              duration: 3000,
              position: "top",
              isClosable: true,
            });

            // Trigger a custom event to refresh the data
            window.dispatchEvent(new CustomEvent("refreshAppointments"));
          } else {
            // Show warning toast if response is empty
            toast({
              title: "Update Failed",
              description: "No response received from server",
              status: "warning",
              duration: 3000,
              position: "top",
              isClosable: true,
            });
          }
        } catch (error) {
          console.error("Error updating status:", error);
          // Show error toast
          toast({
            title: "Error",
            description:
              error.response?.data?.message ||
              "Failed to update appointment status",
            status: "error",
            duration: 3000,
            position: "top",
            isClosable: true,
          });
        }
      };

      return (
        <Button
          colorScheme={color}
          size="sm"
          w="100%"
          onClick={handleStatusClick}
          isDisabled={!["BOOKED", "ARRIVED"].includes(normalizedStatus)}
          variant="solid"
          cursor={
            ["BOOKED", "ARRIVED"].includes(normalizedStatus)
              ? "pointer"
              : "default"
          }
          _disabled={{
            bg:
              color === "green"
                ? "green.500"
                : color === "red"
                  ? "red.500"
                  : color === "purple"
                    ? "purple.500"
                    : color === "blue"
                      ? "blue.500"
                      : `${color}.500`,
            opacity: 1,
            cursor: "default",
          }}
        >
          {label.replace("_", " ")}
        </Button>
      );
    },
  },
  {
    Header: "ACTIONS",
    accessor: "actions",
    Cell: ({ row }) => {
      const toast = useToast();

      // const handleDelete = async (e) => {
      //   e.preventDefault();
      //   e.stopPropagation();

      //   const appointmentId = row.original.id || row.original.appointment_id;
      //   if (!appointmentId) {
      //     console.error("No appointment ID found for deletion");
      //     return;
      //   }

      //   // Show confirmation dialog
      //   if (
      //     window.confirm(
      //       "Are you sure you want to delete this appointment? This action cannot be undone.",
      //     )
      //   ) {
      //     try {
      //       await axiosInstance.delete(
      //         `/api/appointment/delete/${appointmentId}`,
      //       );
      //       toast({
      //         title: "Deleted",
      //         description: "Appointment deleted successfully.",
      //         status: "success",
      //         duration: 3000,
      //         isClosable: true,
      //       });

      //       // Check if immediate delete callback is available (for frontdesk)
      //       if (row.column.onImmediateDelete) {
      //         row.column.onImmediateDelete(appointmentId);
      //       } else {
      //         // Trigger refresh event for other components
      //         window.dispatchEvent(new CustomEvent("refreshAppointments"));
      //       }
      //     } catch (error) {
      //       console.error("Error deleting appointment:", error);
      //       toast({
      //         title: "Error",
      //         description:
      //           error.response?.data?.message ||
      //           "Failed to delete appointment.",
      //         status: "error",
      //         duration: 5000,
      //         isClosable: true,
      //       });
      //     }
      //     // reload the page on modal close to ensure latest data is reflected
      //     try {
      //       if (
      //         typeof window !== "undefined" &&
      //         window.location &&
      //         typeof window.location.reload === "function"
      //       ) {
      //         window.location.reload();
      //       }
      //     } catch (err) {
      //       console.error(
      //         "Error reloading page on EditAppointmentModal close:",
      //         err,
      //       );
      //     }
      //   }
      // };

      const handleCancel = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const appointmentId = row.original.id || row.original.appointment_id;
        if (!appointmentId) {
          console.error("No appointment ID found for cancellation");
          return;
        }

        // Create a custom dialog with appointment details and reason input
        const appointmentDetails = `
Appointment Details:
Appointment ID: ${appointmentId}
Patient: ${row.original.name}
Date: ${new Date(row.original.created_date).toLocaleDateString()}
Time: ${row.original.booking_time}
Doctor: ${row.original.doctor_name}
Services: ${row.original.services ? row.original.services.join(", ") : "N/A"}
Type: ${row.original.treatment_type === "in_clinic" ? "In Clinic" : "Online"}
        `;

        const reason = window.prompt(
          `${appointmentDetails}\n\nPlease provide a reason for cancellation:`,
        );

        if (reason === null) {
          // User clicked cancel
          return;
        }

        if (!reason.trim()) {
          toast({
            title: "Error",
            description: "Cancellation reason is required.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        try {
          // First, get the current bill details to calculate refund
          const billResponse = await axiosInstance.get(
            `/api/bills/byAppointment?appointment_id=${appointmentId}`,
          );
          const billsData = billResponse.data;

          // Handle array response - get the first bill or the bills array
          const currentBill = billsData.bills
            ? billsData.bills[0]
            : Array.isArray(billsData)
              ? billsData[0]
              : billsData;

          if (!currentBill) {
            toast({
              title: "Error",
              description: "No bill found for this appointment.",
              status: "error",
              duration: 3000,
              isClosable: true,
            });
            return;
          }

          const totalAmount = parseFloat(currentBill.total_amt || 0);
          const receivedAmount = parseFloat(currentBill.received_amt || 0);

          // Calculate refund amount (received amount becomes the refund)
          const refundAmount = receivedAmount;
          const pendingAmount = -refundAmount; // pending = amount to be refunded

          // Prepare updated bill data for refund.
          // The API expects a structured payload with a `services` array. Build that from
          // possible comma-separated fields (service, unit_price, discount). If the
          // structured update fails, retry once with the legacy flat object.
          const parseList = (v) => (v ? v.toString().split(",").map((s) => s.trim()) : []);
          const servicesList = parseList(currentBill.service);
          const unitList = parseList(currentBill.unit_price);
          const discountList = parseList(currentBill.discount);

          const servicesPayload = (servicesList.length > 0
            ? servicesList.map((svc, idx) => ({
              serviceName: svc || `Service ${idx + 1}`,
              unitPrice: Number(unitList[idx] ? parseFloat(unitList[idx]) : 0) || 0,
              discount: Number(discountList[idx] ? parseFloat(discountList[idx]) : 0) || 0,
            }))
            : [
              // If no individual services are available, send one service equal to total
              {
                serviceName: "Service",
                unitPrice: Number(totalAmount) || 0,
                discount: 0,
              },
            ]);

          const structuredPayload = {
            consultation_type: currentBill.consultation_type || null,
            total_amt: Number(totalAmount) || 0,
            services: servicesPayload,
            bill_description: `CANCELLED - Reason: ${reason.trim()}. Original bill refunded.`,
            received_amt: Number(receivedAmount) || 0,
            pending_amt: Number(pendingAmount) || 0,
            bill_invoice: currentBill.bill_invoice || null,
          };

          // Legacy flat payload fallback (kept for retry)
          const legacyRefundBillData = {
            id: currentBill.id,
            appointment_id: appointmentId,
            prescription_id: currentBill.prescription_id,
            service: currentBill.service,
            unit_price: currentBill.unit_price,
            discount: currentBill.discount,
            consultation_type: currentBill.consultation_type,
            total_amt: totalAmount.toFixed(2),
            bill_description: `CANCELLED - Reason: ${reason.trim()}. Original bill refunded.`,
            payment_receipt: currentBill.payment_receipt,
            received_amt: "0.00",
            pending_amt: pendingAmount.toFixed(2), // Amount to be refunded
            bill_invoice: currentBill.bill_invoice,
            created_date: currentBill.created_date,
            appointment_date: currentBill.appointment_date,
            appointment_time: currentBill.appointment_time,
            appointment_status: "CANCELLED",
            payment_action: "REFUND",
            doctor_name: currentBill.doctor_name,
            patient_name: currentBill.patient_name,
            patient_phone: currentBill.patient_phone,
            payment_receipt_urls: currentBill.payment_receipt_urls,
            cancellation_reason: reason.trim(),
          };

          // Update the bill with refund details. Try structured payload first; on failure retry once with legacy payload.
          const authToken =
            localStorage.getItem("token") ||
            localStorage.getItem("Token") ||
            "";

          try {
            await axiosInstance.put(
              `/api/bills/update/${currentBill.id}`,
              structuredPayload,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${authToken}`,
                },
              },
            );
          } catch (errStructured) {
            console.warn("Structured bill update failed, retrying with legacy payload:", errStructured?.message || errStructured);
            // Retry once with legacy format
            try {
              await axiosInstance.put(
                `/api/bills/update/${currentBill.id}`,
                legacyRefundBillData,
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                  },
                },
              );
            } catch (errLegacy) {
              console.warn("Both structured and legacy bill update attempts failed:", errLegacy);
              // Treat this as a non-blocking (light) error: inform user but continue the flow
              try {
                toast({
                  title: "Bill update failed",
                  description:
                    errLegacy.response?.data?.message ||
                    "Server rejected bill update. The appointment will still be cancelled locally.",
                  status: "warning",
                  duration: 6000,
                  isClosable: true,
                });
              } catch (toastErr) {
                console.warn("Failed to show toast for bill update failure", toastErr);
              }
              // continue without returning — do not block the cancellation flow
            }
          }

          // if (updateResponse.data) {
          // Update appointment status to cancelled
          await axiosInstance.post("/api/appointment/updateApptStatus", {
            id: appointmentId,
            cancellation_reason: reason.trim(),
            status: "CANCELLED",
          });
          const data = JSON.stringify({
            appointment_id: appointmentId,
            payment_action: pendingAmount < 0 ? "REFUND-PENDING" : "REFUNDED",
          });
          await axiosInstance.post(
            `/api/appointment/updatePaymentStatus`,
            data,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
              },
            },
          );

          // Use statically imported generateBillPdf and uploadFile from Bill component

          // Temporarily create a bill element for PDF generation
          const tempBillContainer = document.createElement("div");
          tempBillContainer.id = "temp-refund-bill";
          tempBillContainer.style.position = "absolute";
          tempBillContainer.style.left = "-9999px";
          document.body.appendChild(tempBillContainer);

          // Create React element for the refund bill (this is a simplified approach)
          // In a real implementation, you'd render the Bill component properly
          tempBillContainer.innerHTML = `
                <div id="refund-bill-preview" style="width: 794px; background: white; padding: 24px;">
                  <div style="border-bottom: 2px solid #000; padding-bottom: 16px; margin-bottom: 16px;">
                    <h2 style="text-align: center; font-size: 24px; margin-bottom: 16px;">REFUND BILL / INVOICE</h2>
                  </div>
                  <div style="margin-bottom: 16px;">
                    <h3>Patient Information</h3>
                    <p>Name: ${currentBill.patient_name}</p>
                    <p>Phone: ${currentBill.patient_phone || currentBill.phone_no || ''}</p>
                    <p>Date: ${currentBill.appointment_date}</p>
                    <p>Time: ${currentBill.appointment_time}</p>
                  </div>
                  <div style="margin-bottom: 16px;">
                    <h3>Refund Details</h3>
                    <p>Total Amount: ₹${totalAmount.toFixed(2)}</p>
                    <p>Amount Received: ₹${receivedAmount.toFixed(2)}</p>
                    <p style="color: red; font-weight: bold;">${pendingAmount < 0 ? `Refund Amount: ₹${Math.abs(refundAmount)}` : `Pending Amount: ₹${pendingAmount.toFixed(2)}`}</p>
                    <p>Reason: ${reason.trim()}</p>
                    <p>Payment Status: REFUND PENDING </p>
                    <p>Appointment Status: CANCELLED </p>
                  </div>
                </div>
              `;

          // Generate PDF from the temporary element
          const refundPdfBlob = await generateBillPdf(
            "refund-bill-preview",
            false,
          );

          // Upload the refund bill PDF
          const refundBillUrl = await uploadFile(refundPdfBlob, {
            fileName: `refund_bill_${appointmentId}_${Date.now()}.pdf`,
            mimeType: "application/pdf",
          });

          if (refundBillUrl) {
            try {
              await axiosInstance.put(
                `/api/bills/update/${currentBill.id}`,
                {
                  ...legacyRefundBillData,
                  bill_invoice: refundBillUrl,
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                  },
                },
              );
            } catch (errPutInvoice) {
              // Non-blocking: invoice update failed, log and show a gentle warning
              console.warn("Failed to attach bill_invoice after upload:", errPutInvoice);
              try {
                toast({
                  title: "Invoice attach failed",
                  description:
                    errPutInvoice.response?.data?.message ||
                    "Uploaded invoice could not be attached to the bill.",
                  status: "warning",
                  duration: 5000,
                  isClosable: true,
                });
              } catch (tErr) {
                console.warn("Failed to show toast for invoice attach failure", tErr);
              }
            }
          }

          alert(
            `Appointment cancelled successfully. Refund of ₹${refundAmount.toFixed(2)} is pending.`,
          );
          toast({
            title: "Appointment Cancelled",
            description: `Appointment cancelled successfully. Refund of ₹${refundAmount.toFixed(2)} is pending.`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });

          // let config = {
          //   method: 'post',
          //   maxBodyLength: Infinity,
          //   url: '/api/appointment/updatePaymentStatus',
          //   headers: {
          //     'Content-Type': 'application/json',
          //     'Authorization': `Bearer ${authToken}`
          //   },
          //   data: data
          // };

          // async function updatePaymentStatus() {
          //   try {
          //     const response = await axios.request(config);
          //     console.log(JSON.stringify(response.data));
          //   }
          //   catch (error) {
          //     console.log(error);
          //   }
          // }

          // updatePaymentStatus();
          // Trigger refresh
          window.dispatchEvent(new CustomEvent("refreshAppointments"));
          // }
        } catch (error) {
          console.error("Error cancelling appointment:", error);
          toast({
            title: "Error",
            description:
              error.response?.data?.message || "Failed to cancel appointment.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
        // reload the page on modal close to ensure latest data is reflected
        // try {
        //   if (
        //     typeof window !== "undefined" &&
        //     window.location &&
        //     typeof window.location.reload === "function"
        //   ) {
        //     window.location.reload();
        //   }
        // } catch (err) {
        //   console.error(
        //     "Error reloading page on EditAppointmentModal close:",
        //     err,
        //   );
        // }
      };

      // Simple, explicit conditional rendering rules based on status and payment_action
      // Rules (priority: status normalized):
      // - BOOKED or ARRIVED + PAID  => Edit + Cancel
      // - BOOKED or ARRIVED + PENDING => Edit + Delete
      // - COMPLETED => Edit (ignore payment)
      // - CANCELLED => show nothing

      const status = String(row.original.status || "").toUpperCase();
      const payment = String(row.original.payment_action || "").toUpperCase();

      const showEdit = ["BOOKED", "MISSED", "ARRIVED", "COMPLETED"].includes(
        status,
      );
      const showCancel =
        ((status === "BOOKED" || status === "MISSED" || status === "ARRIVED") &&
          payment === "PAID") ||
        payment === "PENDING";
      // const showDelete =
      //   (status === "BOOKED" || status === "MISSED" || status === "ARRIVED") &&
      //   payment === "PENDING";

      // Simple edit handler mapping (keeps existing mapping logic)
      const handleEditClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log("Edit clicked for row:", row.original);
        const appointmentData = {
          id: row.original.id || row.original.appointment_id,
          patientName: row.original.name || row.original.patient_name,
          patientId: row.original.id || row.original.patient_id,
          patientGender: row.original.gender || row.original.patient_gender,
          patientAge: row.original.age || row.original.patient_age,
          phone_no: row.original.phoneNumber || row.original.phone_no,
          appointmentDate:
            row.original.created_date || row.original.appointment_date,
          appointmentTime:
            row.original.booking_time || row.original.appointment_time,
          appointmentType:
            row.original.treatment_type || row.original.appointment_type,
          doctorId: row.original.doctor_id,
          isEmergency: row.original.is_emergency || 0,
          services: row.original.services || [],
        };
        row.original.onEdit && row.original.onEdit(appointmentData);
      };

      return (
        <Flex gap={2}>
          {showEdit && (
            <IconButton
              icon={<FiEdit />}
              aria-label="Edit"
              size="sm"
              border="0px"
              colorScheme="blue"
              borderRadius="full"
              fontWeight="bold"
              onClick={handleEditClick}
            />
          )}

          {showCancel && (
            <IconButton
              icon={<div style={{ fontSize: "1rem", color: "white" }}>X</div>}
              aria-label="Cancel"
              size="sm"
              border="0px"
              colorScheme="red"
              borderRadius="full"
              fontWeight="bold"
              onClick={handleCancel}
            />
          )}

          {/* {showDelete && (
            <IconButton
              icon={<DeleteIcon />}
              aria-label="Delete"
              size="sm"
              colorScheme="red"
              borderRadius="full"
              fontWeight="bold"
              onClick={handleDelete}
            />
          )}*/}
        </Flex>
      );
    },
  },
];

const doctorcolumnsDataComplexRaw = [
  {
    Header: "Queue",
    accessor: "queue",
  },
  {
    Header: "PATIENT ID",
    accessor: "patient_id",
  },
  {
    Header: "TOKEN NO",
    accessor: "token_no",
  },
  {
    Header: "PATIENT NAME",
    accessor: "name",
  },
  {
    Header: "AGE",
    accessor: "age",
  },
  {
    Header: "SEX",
    accessor: "gender",
  },
  {
    Header: "MOBILE NO.",
    accessor: "phoneNumber",
  },
  {
    Header: "PAST VISIT",
    accessor: "past_appointments",
  },
  {
    Header: "TYPE",
    accessor: "type",
    Cell: ({ value, row }) => {
      const isOnline = String(value || "").toLowerCase() === "online";
      const hasMeetLink = !!(
        row?.original?.meet_link ||
        row?.original?.meeting_link ||
        row?.original?.meetLink ||
        row?.original?.meetUrl
      );
      return (
        <Flex alignItems="center" gap={2}>
          <Text>{value}</Text>
          {(isOnline || hasMeetLink) && (
            <IconButton
              icon={<MdVideoCall style={{ fontSize: "1rem" }} />}
              aria-label="Join Video Call"
              size="sm"
              colorScheme="blue"
              variant="ghost"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                // Get the meeting link from the row data
                const meetLink =
                  row?.original?.meet_link ||
                  row?.original?.meeting_link ||
                  row?.original?.meetLink ||
                  row?.original?.meetUrl ||
                  null;

                if (meetLink) {
                  // Open the meeting link in a new tab
                  window.open(meetLink, "_blank", "noopener,noreferrer");
                  console.log("Opening meeting link in new tab:", meetLink);
                } else {
                  console.warn(
                    "No meeting link available for this consultation",
                  );
                  // You could show a toast notification here if needed
                }
              }}
              title={
                row.original.meet_link
                  ? "Join Video Call"
                  : "No meeting link available"
              }
              isDisabled={!hasMeetLink}
              cursor={hasMeetLink ? "pointer" : "not-allowed"}
            />
          )}
        </Flex>
      );
    },
  },
  {
    Header: "SERVICE",
    accessor: "service_name",
  },
];

const billscolumnsDataComplexRaw = [
  {
    Header: "Appointment Date",
    accessor: "appointment_date",
    Cell: ({ value }) => {
      if (!value) return "";
      try {
        const date = new Date(value);
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return <Text color="gray.600">{`${day}-${month}-${year}`}</Text>;
      } catch {
        return <Text color="gray.600">{value}</Text>;
      }
    },
  },
  {
    Header: "Patient Name ",
    accessor: "patient_name",
  },
  {
    Header: "Consultation Type",
    accessor: "consultation_type",
    Cell: ({ value }) => (
      <Text>{value === "in_clinic" ? "In Clinic" : "Online"}</Text>
    ),
  },
  {
    Header: "Service",
    accessor: "service",
    Cell: ({ value }) => (
      <Text>
        {(value || "").toString().split(",").filter(Boolean).map((item, index) => (
          <div key={index}>{String(item).trim()}</div>
        ))}
      </Text>
    ),
  },
  {
    Header: "Unit Price",
    accessor: "unit_price",
    Cell: ({ value }) => (
      <Text>
        {(value || "").toString().split(",").filter(Boolean).map((item, index) => (
          <div key={index}>₹{String(item).trim()}</div>
        ))}
      </Text>
    ),
  },
  {
    Header: "Discount",
    accessor: "discount",
    Cell: ({ value }) => (
      <Text>
        {(value || "").toString().split(",").filter(Boolean).map((item, index) => (
          <div key={index}> {String(item).trim() !== "0" ? String(item).trim() : "-"}</div>
        ))}
      </Text>
    ),
  },
  {
    Header: "Total Amount",
    accessor: "total_amt",
    Cell: ({ value }) => <Text>₹{value}</Text>,
  },

  {
    Header: "Recived Amount",
    accessor: "received_amt",
  },
  {
    Header: "Pending Amount",
    accessor: "pending_amt",
  },
  {
    Header: "Payment Status",
    accessor: "payment_action",
    Cell: ({ value, row }) => <Text>{value}</Text>,
  },
  {
    Header: "Payment Receipt",
    accessor: "payment_receipt_urls",
  },
  {
    Header: "Bill Invoice",
    accessor: "bill_invoice",
  },
];

const patientColumnsDataComplexRaw = [
  {
    Header: "PATIENT ID",
    accessor: "patient_id",
  },
  {
    Header: "NAME",
    accessor: "name",
  },
  {
    Header: "GENDER",
    accessor: "gender",
  },
  {
    Header: "AGE",
    accessor: "age",
  },
  {
    Header: "EMAIL",
    accessor: "email",
  },
  {
    Header: "PHONE NO",
    accessor: "number",
  },
  {
    Header: "PAST APPOINMENTS",
    accessor: "past_appointments",
  },
  {
    Header: "LAB TESTS",
    accessor: "lab_tests",
  },
  {
    Header: "BOOK APPOINMENTS",
    accessor: "book_appointments",
  },
  {
    Header: "EMERGENCY",
    accessor: "emergency",
    Cell: ({ row }) => (
      <IconButton
        icon={<AmbulanceIcon size={24} />}
        aria-label="Emergency Appointment"
        size="sm"
        colorScheme="red"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (row.original.book_appointments) {
            row.original.book_appointments(true);
          }
        }}
      />
    ),
  },
];

// processed exports are appended after raw definitions

export const columnsDataCheck = applyDefaultCell(columnsDataCheckRaw);
export const columnsDataConsultation = applyDefaultCell(columnsDataConsultationRaw);
export const columnsDataComplex = applyDefaultCell(columnsDataComplexRaw);
export const clinicColumnsData = applyDefaultCell(clinicColumnsDataRaw);
export const frontdeskColumnsData = applyDefaultCell(frontdeskColumnsDataRaw);
export const appointmentcolumnsDataComplex = applyDefaultCell(appointmentcolumnsDataComplexRaw);
export const doctorcolumnsDataComplex = applyDefaultCell(doctorcolumnsDataComplexRaw);
export const billscolumnsDataComplex = applyDefaultCell(billscolumnsDataComplexRaw);
export const patientColumnsDataComplex = applyDefaultCell(patientColumnsDataComplexRaw);

const auditLogsColumnsDataRaw = [
  {
    Header: "Time",
    accessor: "created_at",
    Cell: ({ value }) => {
      if (!value) return "—";
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
    },
  },
  {
    Header: "Actor",
    accessor: "actor_label",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "Action",
    accessor: "action",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "Entity",
    accessor: "entity_label",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "Clinic",
    accessor: "clinic_id",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "Summary",
    accessor: "summary",
    Cell: ({ value }) => formatDisplayValue(value),
  },
];

const loginActivityColumnsDataRaw = [
  {
    Header: "Login time",
    accessor: "login_time",
    Cell: ({ value }) => {
      if (!value) return "—";
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
    },
  },
  {
    Header: "User ID",
    accessor: "user_id",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "Role",
    accessor: "role",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "Last activity",
    accessor: "last_activity",
    Cell: ({ value }) => {
      if (!value) return "—";
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
    },
  },
];

const googleAuthLogsColumnsDataRaw = [
  {
    Header: "Time",
    accessor: "created_at",
    Cell: ({ value }) => {
      if (!value) return "—";
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
    },
  },
  {
    Header: "Action",
    accessor: "action",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "Admin user",
    accessor: "admin_user_id",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "Success",
    accessor: "success",
    Cell: ({ value }) => (value ? "Yes" : "No"),
  },
  {
    Header: "IP",
    accessor: "ip_address",
    Cell: ({ value }) => formatDisplayValue(value),
  },
  {
    Header: "Error",
    accessor: "error_message",
    Cell: ({ value }) => formatDisplayValue(value),
  },
];

export const auditLogsColumnsData = applyDefaultCell(auditLogsColumnsDataRaw);
export const loginActivityColumnsData = applyDefaultCell(loginActivityColumnsDataRaw);
export const googleAuthLogsColumnsData = applyDefaultCell(googleAuthLogsColumnsDataRaw);
